(() => {
  const form = document.getElementById('TdaCartForm');
  if (!form) return;

  const root = window.Shopify?.routes?.root || '/';
  const section = form.closest('.tda-cart');
  const sectionId = section.dataset.sectionId;
  const sectionIds = [sectionId, 'cart-icon-bubble'];
  const cartStatus = form.querySelector('[data-tda-cart-status]');
  let giftCart = JSON.parse(document.querySelector('[data-tda-cart-gift-state]').textContent);
  let updatingCart = false;
  let savingGift = false;
  let cartNeedsRefresh = false;

  const requestCart = async (endpoint, body) => {
    const response = await fetch(`${root}cart${endpoint}.js`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      cache: 'no-store',
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || section.dataset.cartError);
    return data;
  };

  const getRenderedCart = async (cart) => {
    let sections = cart.sections;
    if (!sections?.[sectionId] || !sections?.['cart-icon-bubble']) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds.join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(section.dataset.cartError);
      sections = await response.json();
    }
    if (!sections?.[sectionId] || !sections?.['cart-icon-bubble']) throw new Error(section.dataset.cartError);
    const rendered = new DOMParser().parseFromString(sections[sectionId], 'text/html');
    if (!rendered.querySelector('.tda-cart')) throw new Error(section.dataset.cartError);
    return { rendered, bubble: sections['cart-icon-bubble'] };
  };

  const applyCart = async (cart, trigger, restoreFocus = false) => {
    const { rendered, bubble } = await getRenderedCart(cart);
    const scrollTop = window.scrollY;
    const scrollLeft = window.scrollX;
    const currentRow = trigger?.closest('[data-tda-line-key]');
    const rows = [...form.querySelectorAll('[data-tda-line-key]')];
    const index = rows.indexOf(currentRow);
    const focused = restoreFocus;
    const openGiftRows = rows.filter(row => row.querySelector('details[open]')).map(row => row.dataset.tdaLineKey);
    let focusTarget;
    if (cart.item_count === 0) {
      const empty = rendered.querySelector('.tda-cart__empty');
      if (!empty) throw new Error(section.dataset.cartError);
      section.replaceChildren(empty);
      focusTarget = empty.querySelector('h2');
      if (focusTarget) focusTarget.tabIndex = -1;
    } else {
      const items = rendered.querySelector('.tda-cart__items');
      const summary = rendered.querySelector('.tda-cart__summary');
      if (!items || !summary) throw new Error(section.dataset.cartError);
      form.querySelector('.tda-cart__items').replaceWith(items);
      form.querySelector('.tda-cart__summary').replaceWith(summary);
      const subtitle = rendered.querySelector('.tda-cart-gifting__gift-copy small');
      const currentSubtitle = form.querySelector('.tda-cart-gifting__gift-copy small');
      if (subtitle && currentSubtitle) currentSubtitle.textContent = subtitle.textContent;
      const gifting = form.querySelector('.tda-cart-gifting');
      if (gifting) gifting.hidden = !rendered.querySelector('.tda-cart-gifting');
      const bag = form.querySelector('[data-tda-shopping-bag]');
      if (bag) bag.checked = cart.items.some(item => item.properties?.['Gift bag'] === 'Yes');
      const nextRows = [...items.querySelectorAll('[data-tda-line-key]')];
      nextRows.forEach(row => {
        if (openGiftRows.includes(row.dataset.tdaLineKey)) row.querySelector('details')?.setAttribute('open', '');
      });
      const matchingRow = nextRows.find(row => row.dataset.tdaLineKey === currentRow?.dataset.tdaLineKey);
      const nextRow = matchingRow || nextRows[Math.min(Math.max(index, 0), nextRows.length - 1)];
      const selector = trigger?.matches('[data-tda-qty-plus]') ? '[data-tda-qty-plus]'
        : trigger?.matches('[data-tda-qty-minus]') ? '[data-tda-qty-minus]' : '.tda-cart-item__remove';
      focusTarget = nextRow?.querySelector(selector);
      bindQuantityControls();
      hydrateImages();
    }
    giftCart = cart;
    const state = document.querySelector('[data-tda-cart-gift-state]');
    if (state) state.textContent = JSON.stringify(cart);
    const count = document.getElementById('cart-icon-bubble');
    if (count) count.innerHTML = bubble;
    const countText = new DOMParser().parseFromString(bubble, 'text/html').querySelector('.cart-count-bubble .visually-hidden')?.textContent;
    if (focused) focusTarget?.focus({ preventScroll: true });
    window.scrollTo({ top: cart.item_count ? scrollTop : 0, left: scrollLeft, behavior: 'instant' });
    cartNeedsRefresh = false;
    cartStatus.classList.add('visually-hidden');
    cartStatus.textContent = cart.item_count ? countText || '' : focusTarget?.textContent || '';
    document.dispatchEvent(new CustomEvent('tda:cart:refresh'));
  };

  const updateLine = async (trigger, quantity) => {
    if (updatingCart || savingGift) return;
    const row = trigger.closest('[data-tda-line-key]');
    if (!row) return;
    const restoreFocus = document.activeElement === trigger;
    updatingCart = true;
    cartNeedsRefresh = true;
    cartStatus.textContent = '';
    form.setAttribute('aria-busy', 'true');
    const controls = [...form.querySelectorAll('button, input')].map(control => [control, control.disabled]);
    controls.forEach(([control]) => { control.disabled = true; });
    form.querySelectorAll('.tda-cart-item__remove').forEach(link => link.setAttribute('aria-disabled', 'true'));
    try {
      const cart = await requestCart('/change', {
        id: row.dataset.tdaLineKey,
        quantity,
        sections: sectionIds,
        sections_url: window.location.pathname
      });
      await applyCart(cart, trigger, restoreFocus);
    } catch (error) {
      try {
        await applyCart(await requestCart(''), trigger, restoreFocus);
      } catch (_) {}
      cartStatus.classList.remove('visually-hidden');
      cartStatus.textContent = error.message || section.dataset.cartError;
    } finally {
      updatingCart = false;
      form.removeAttribute('aria-busy');
      controls.forEach(([control, disabled]) => { control.disabled = disabled; });
      form.querySelectorAll('.tda-cart-item__remove').forEach(link => link.removeAttribute('aria-disabled'));
      form.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = cartNeedsRefresh || savingGift; });
    }
  };

  form.addEventListener('click', event => {
    const remove = event.target.closest('.tda-cart-item__remove');
    if (!remove) return;
    event.preventDefault();
    updateLine(remove, 0);
  });
  form.addEventListener('submit', event => {
    if (updatingCart || savingGift || cartNeedsRefresh) event.preventDefault();
  });

  const bindQuantityControls = () => form.querySelectorAll('[data-tda-qty]').forEach(control => {
    if (control.dataset.bound) return;
    control.dataset.bound = 'true';
    const input = control.querySelector('.tda-cart-qty__input');
    const minus = control.querySelector('[data-tda-qty-minus]');
    const plus = control.querySelector('[data-tda-qty-plus]');
    if (!input || !minus || !plus) return;
    minus.addEventListener('click', () => {
      const current = Number.parseInt(input.value, 10) || 1;
      if (current > 1) updateLine(minus, current - 1);
    });
    plus.addEventListener('click', () => updateLine(plus, (Number.parseInt(input.value, 10) || 1) + 1));
  });
  bindQuantityControls();

  /* Reservation items use one central Shopify product, so its line item can have no useful image.
     Resolve the visible creation (for example “Gesture”) against the storefront product search
     and use that creation's product image without changing the cart's spacing. */
  const productImageCache = new Map();
  const normalizeCreation = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  const resolveCreationImage = async (creation) => {
    const key = normalizeCreation(creation);
    if (!key) return '';
    if (productImageCache.has(key)) return productImageCache.get(key);

    const request = (async () => {
      try {
        const params = new URLSearchParams();
        params.set('q', creation);
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '10');
        params.set('resources[options][unavailable_products]', 'show');

        const response = await fetch(`${root}search/suggest.json?${params.toString()}`, {
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) return '';
        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        const exact = products.find((product) => normalizeCreation(product.title) === key);
        const close = products.find((product) => normalizeCreation(product.title).includes(key));
        const product = exact || close || products[0];
        if (!product) return '';

        if (typeof product.image === 'string') return product.image;
        if (typeof product.featured_image === 'string') return product.featured_image;
        if (product.featured_image?.url) return product.featured_image.url;
        if (product.image?.url) return product.image.url;
        return '';
      } catch (_) {
        return '';
      }
    })();

    productImageCache.set(key, request);
    return request;
  };

  const hydrateImages = () => document.querySelectorAll('[data-tda-cart-item-media]').forEach(async (media) => {
    const missingImage = media.querySelector('[data-tda-cart-item-image]');
    if (!missingImage) return;

    const creation = media.dataset.tdaCreation || '';
    const imageUrl = await resolveCreationImage(creation);
    if (!imageUrl) return;

    const placeholder = media.querySelector('[data-tda-cart-item-placeholder]');
    missingImage.src = imageUrl;
    missingImage.hidden = false;
    if (placeholder) placeholder.hidden = true;
  });

  hydrateImages();

  const html = document.documentElement;
  let activeDrawer = null;
  let activeCloseTimer = null;
  let lastFocused = null;

  const closeActiveDrawer = () => {
    if (!activeDrawer) return;
    window.clearTimeout(activeCloseTimer);

    const drawer = activeDrawer;
    drawer.classList.remove('is-open');
    drawer.classList.add('is-closing');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    html.classList.remove('tda-modal-is-open', 'tda-delivery-is-open');

    activeCloseTimer = window.setTimeout(() => {
      drawer.classList.remove('is-closing');
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    }, 1400);

    activeDrawer = null;
  };

  const openDrawer = (drawer, panel, htmlClass = 'tda-modal-is-open') => {
    if (!drawer || !panel) return;
    if (activeDrawer === drawer) return;
    if (activeDrawer) closeActiveDrawer();

    window.clearTimeout(activeCloseTimer);
    lastFocused = document.activeElement;
    activeDrawer = drawer;
    drawer.classList.remove('is-closing');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.inert = false;
    html.classList.add(htmlClass);
    window.requestAnimationFrame(() => panel.focus({ preventScroll: true }));
  };

  const setupDrawer = ({ drawer, openButtons, closeButtons, htmlClass }) => {
    if (!drawer) return;
    const panel = drawer.querySelector('[role="dialog"]');
    openButtons.forEach((button) => button.addEventListener('click', () => openDrawer(drawer, panel, htmlClass)));
    closeButtons.forEach((button) => button.addEventListener('click', closeActiveDrawer));
  };

  const deliveryDrawer = document.querySelector('[data-tda-delivery-drawer]');
  setupDrawer({
    drawer: deliveryDrawer,
    openButtons: [],
    closeButtons: deliveryDrawer ? [...deliveryDrawer.querySelectorAll('[data-tda-delivery-close]')] : [],
    htmlClass: 'tda-delivery-is-open'
  });

  form.addEventListener('click', event => {
    if (event.target.closest('[data-tda-delivery-open]')) openDrawer(deliveryDrawer, deliveryDrawer?.querySelector('[role="dialog"]'), 'tda-delivery-is-open');
  });

  const giftDrawer = document.querySelector('[data-tda-editorial-drawer="gift"]');
  setupDrawer({
    drawer: giftDrawer,
    openButtons: [...document.querySelectorAll('[data-tda-gift-open]')],
    closeButtons: giftDrawer ? [...giftDrawer.querySelectorAll('[data-tda-editorial-close]')] : [],
    htmlClass: 'tda-modal-is-open'
  });

  const packagingDrawer = document.querySelector('[data-tda-editorial-drawer="packaging"]');
  setupDrawer({
    drawer: packagingDrawer,
    openButtons: [...document.querySelectorAll('[data-tda-packaging-open]')],
    closeButtons: packagingDrawer ? [...packagingDrawer.querySelectorAll('[data-tda-editorial-close]')] : [],
    htmlClass: 'tda-modal-is-open'
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeDrawer) {
      closeActiveDrawer();
    }
  });

  /* Packaging editorial video controls */
  document.querySelectorAll('[data-tda-video-wrap]').forEach((wrap) => {
    const video = wrap.querySelector('video');
    const toggle = wrap.querySelector('[data-tda-video-toggle]');
    if (!video || !toggle) return;

    const syncVideoButton = () => {
      const playing = !video.paused;
      toggle.textContent = playing ? 'Ⅱ' : '▶';
      toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    };

    toggle.addEventListener('click', () => {
      if (video.paused) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      } else {
        video.pause();
      }
      window.setTimeout(syncVideoButton, 0);
    });

    video.addEventListener('play', syncVideoButton);
    video.addEventListener('pause', syncVideoButton);
    syncVideoButton();
  });

  const giftForm = document.querySelector('[data-tda-gift-form]');
  const shoppingBagToggle = document.querySelector('[data-tda-shopping-bag]');
  if (!giftForm || !shoppingBagToggle) return;
  const labels = JSON.parse(document.querySelector('[data-tda-cart-gift-labels]').textContent);
  const eligible = cart => cart.items.filter(item => !item.gift_card);
  const textarea = giftForm.querySelector('[data-tda-gift-message]');
  const signature = giftForm.querySelector('[data-tda-gift-signature]');
  const status = giftForm.querySelector('[data-tda-gift-status]');
  const saveButton = giftForm.querySelector('[data-tda-gift-save]');
  const bagStatus = document.querySelector('[data-tda-gifting-status]');
  const syncPreview = () => {
    [textarea,signature].forEach(input => input.closest('.tda-res-gift-field').classList.toggle('is-filled',!!input.value.trim()));
    giftForm.querySelector('[data-tda-gift-counter]').textContent = `${textarea.value.length}/200`;
    const preview = giftDrawer.querySelector('[data-tda-res-gift-preview]');
    preview.textContent = textarea.value.trim() || preview.dataset.placeholder;
    giftDrawer.querySelector('[data-tda-res-gift-preview-signature]').textContent = signature.value;
    giftDrawer.querySelector('.tda-res-gift-art__writing').classList.toggle('is-long',textarea.value.length > 100);
  };
  let originalMessage = '', originalSignature = '';
  const loadGift = () => {
    const items = eligible(giftCart);
    const selected = items.find(item => item.properties?.['Gift message'] || item.properties?.['Gift signature']);
    textarea.value = selected?.properties['Gift message'] || giftCart.attributes?.['Gift message'] || '';
    signature.value = selected?.properties['Gift signature'] || giftCart.attributes?.['Gift signature'] || '';
    shoppingBagToggle.checked = items.some(item => item.properties?.['Gift bag'] === 'Yes') || giftCart.attributes?.['Shopping bag'] === 'Yes';
    originalMessage = textarea.value; originalSignature = signature.value;
    syncPreview();
  };
  const saveGift = async patch => {
    giftCart = await requestCart('');
    const targets = eligible(giftCart);
    for(const item of targets) {
      const properties = {...item.properties};
      if(properties['_TDA gift configured'] !== 'Yes') {
        properties['Gift message'] ||= giftCart.attributes?.['Gift message'] || '';
        properties['Gift signature'] ||= giftCart.attributes?.['Gift signature'] || '';
        properties['Gift bag'] ||= giftCart.attributes?.['Shopping bag'] === 'Yes' ? 'Yes' : 'No';
      }
      Object.assign(properties,patch);
      properties['_TDA gift configured'] = 'Yes';
      properties['Gift presentation'] = properties['Gift message']?.trim() || properties['Gift signature']?.trim() || properties['Gift bag'] === 'Yes' ? 'Yes' : 'No';
      const current = giftCart.items.find(line => line.key === item.key);
      if (!current) continue;
      giftCart = await requestCart('/change',{id:current.key,quantity:current.quantity,properties});
    }
    const sectionId=document.querySelector('.tda-cart').dataset.sectionId;
    giftCart = await requestCart('/update',{attributes:{'Gift message':'','Gift signature':'','No gift card':'','Shopping bag':''},sections:[sectionId],sections_url:window.location.pathname});
    const rendered=giftCart.sections?.[sectionId];
    if(rendered){
      const documentFragment=new DOMParser().parseFromString(rendered,'text/html');
      const anchor=document.querySelector('.tda-cart-gifting');
      const before=anchor.getBoundingClientRect().top;
      const table=documentFragment.querySelector('.tda-cart__items');
      if(table)document.querySelector('.tda-cart__items').replaceWith(table);
      const subtitle=documentFragment.querySelector('.tda-cart-gifting__gift-copy small');
      if(subtitle)document.querySelector('.tda-cart-gifting__gift-copy small').textContent=subtitle.textContent;
      bindQuantityControls(); hydrateImages();
      window.scrollBy(0,anchor.getBoundingClientRect().top-before);
    }
    shoppingBagToggle.checked=eligible(giftCart).some(item=>item.properties?.['Gift bag']==='Yes');
  };
  const setSaving = saving => {
    savingGift = saving;
    shoppingBagToggle.disabled = saving;
    shoppingBagToggle.setAttribute('aria-busy',String(saving));
    saveButton.disabled = saving;
    form.querySelectorAll('[name="checkout"]').forEach(button=>button.disabled=saving || updatingCart || cartNeedsRefresh);
  };
  form.addEventListener('submit',event=>{if(savingGift)event.preventDefault();});
  document.querySelectorAll('[data-tda-gift-open]').forEach(button=>button.addEventListener('click',loadGift));
  [textarea,signature].forEach(input=>input.addEventListener('input',syncPreview));
  giftForm.addEventListener('submit',async event=>{
    event.preventDefault();
    setSaving(true); status.textContent=labels.saving;
    try {
      const patch = {};
      if(textarea.value !== originalMessage) patch['Gift message'] = textarea.value.trim().slice(0,200);
      if(signature.value !== originalSignature) patch['Gift signature'] = signature.value.trim().slice(0,20);
      if(Object.keys(patch).length) await saveGift(patch);
      status.textContent=''; closeActiveDrawer();

    } catch(error) { status.textContent=labels.retry; }
    finally { setSaving(false); }
  });
  shoppingBagToggle.addEventListener('change',async()=>{
    const intended=shoppingBagToggle.checked;
    setSaving(true); bagStatus.textContent='';
    try { await saveGift({'Gift bag':intended?'Yes':'No'}); bagStatus.textContent=''; }
    catch(error) { shoppingBagToggle.checked=!intended; bagStatus.textContent=labels.retry; }
    finally { setSaving(false); }
  });
  giftDrawer.addEventListener('keydown',event=>{
    if(event.key!=='Tab')return;
    const controls=[...giftDrawer.querySelectorAll('button,input,textarea')].filter(el=>!el.disabled && el.getClientRects().length);
    const first=controls[0],last=controls[controls.length-1];
    if(event.shiftKey && document.activeElement===first){event.preventDefault();last.focus();}
    else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first.focus();}
  });
  loadGift();

})();
